import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled from 'styled-components';
import ndsTokens from 'nicollet-tokens';
import { BaseButton } from '../../BaseButton/index.js';
import { ButtonPrimary } from '../../ButtonPrimary/index.js';
import { ButtonSecondary } from '../../ButtonSecondary/index.js';
import { Row } from '../../Row/index.js';
import { Col } from '../../Col/index.js';
import { SelectBoxArrow } from './SelectBoxArrow.js';

const invalidStyles = `
  border-color: ${ndsTokens.colors.border.warning};
  background-color: ${ndsTokens.colors.background.warning};

  &:hover {
    border-color: ${ndsTokens.colors.border.warning};
    background-color: ${ndsTokens.colors.background.warning};
  }

  &:active {
    background-color: ${ndsTokens.colors.background.darkest};
  }

  label {
    color: ${ndsTokens.colors.palette.orange.dark};
  }
`;
const validStyles = `
  border-color: ${ndsTokens.colors.border.success};

  &:hover {
    border-color: ${ndsTokens.colors.border.success};
  }

  label {
    color: ${ndsTokens.colors.palette.green.default};
  }
`; // HACK: this is a hack done as a placeholder until
// design-systems comes up with a better solution
// Design-system's solution is to clean this up once SelectCustom gets developed further

const ReactiveTextCol = styled(({
  variant,
  ...domSafeProps
}) => jsx(Col, Object.assign({}, domSafeProps), void 0))`
  display: flex;
  ${props => props.variant === 'borderless' ? `
      color:  ${ndsTokens.colors.text.onLight.primary};
    ` : ''}
`;
const LabelSpan = styled.span`
  padding-right: ${ndsTokens.space.generic.x1};
  ${props => props.boldLabel ? 'font-weight: bold;' : ''}
  ${props => props.hasStrikethrough ? 'text-decoration: line-through;' : ''}
`;
const ValueSpan = styled.span`
  ${props => props.boldValue ? 'font-weight: bold' : ''};
  ${props => props.hasStrikethrough ? 'text-decoration: line-through;' : ''}
`;
const StyledCol = styled(Col)`
  text-align: right;
  font-size: ${ndsTokens.font.size.text.small};
`;
const BlankButton = styled(BaseButton)`
  border-color: transparent;
`; // exporting the following Styled Component to share with the NativeSelect component

const SelectButtonWithValidation = styled(({
  childRef,
  styleProps,
  display,
  variant,
  ...domSafeProps
}) => {
  if (variant === 'borderless' || variant === 'blank') {
    return jsx(BlankButton, Object.assign({
      ref: childRef
    }, domSafeProps), void 0);
  }

  if (variant === 'primary') {
    return jsx(ButtonPrimary, Object.assign({
      ref: childRef
    }, domSafeProps), void 0);
  }

  return jsx(ButtonSecondary, Object.assign({
    ref: childRef
  }, domSafeProps), void 0);
})`
  ${props => props.styleProps.valid && validStyles};
  ${props => props.styleProps.invalid && invalidStyles};
  ${props => props.display === 'block' && 'display: block; width: 100%;'}

  /* Removes extra box in firefox when focused */
  &::-moz-focus-inner {
    border: 0;
  }
`;
const SelectBox = ({
  customLabelImage,
  label = '',
  boldLabel,
  value = '',
  boldValue,
  display,
  toggleMenu,
  imageUrl,
  $childRef,
  valueRenderer,
  size,
  tabIndex,
  isHidden,
  valid,
  // capture so not spread to Button
  invalid,
  // capture so not spread to Button
  'data-test': testId,
  descriptor,
  hasStrikethrough,
  isOpen,
  arrowLabelValue,
  variant,
  ...restProps
}) => {
  const stringToRender = [label, value].filter(Boolean).join('');
  const stringToRenderSize = stringToRender.length;
  return jsx(SelectButtonWithValidation, Object.assign({
    "aria-hidden": isHidden,
    childRef: $childRef,
    "data-test": testId,
    display: display,
    onClick: toggleMenu,
    size: size,
    styleProps: {
      valid,
      invalid
    },
    tabIndex: tabIndex,
    variant: variant === 'borderless' ? 'blank' : variant
  }, restProps, {
    children: jsxs(Row, Object.assign({
      between: "xs",
      className: "select--customLabel",
      middle: "xs",
      wrap: "nowrap"
    }, {
      children: [jsxs(ReactiveTextCol, Object.assign({
        textLength: stringToRenderSize,
        useRenderer: Boolean(valueRenderer),
        variant: variant
      }, {
        children: [label ? jsx(LabelSpan, Object.assign({
          boldLabel: boldLabel,
          hasStrikethrough: hasStrikethrough
        }, {
          children: label
        }), void 0) : null, value && !valueRenderer ? jsx(ValueSpan, Object.assign({
          boldValue: boldValue,
          hasStrikethrough: hasStrikethrough
        }, {
          children: value
        }), void 0) : valueRenderer]
      }), void 0), jsx(StyledCol, {
        children: jsxs(Fragment, {
          children: [descriptor !== null && descriptor !== void 0 ? descriptor : '', jsx(SelectBoxArrow, {
            arrowLabelValue: arrowLabelValue,
            customLabelImage: customLabelImage,
            imageUrl: imageUrl,
            isOpen: isOpen,
            size: size
          }, void 0)]
        }, void 0)
      }, void 0)]
    }), void 0)
  }), void 0);
};
SelectBox.displayName = 'SelectBox';

export { SelectBox, SelectButtonWithValidation };
